/* eslint-disable eqeqeq */
import React, { useMemo, useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import moment from 'moment';
import { formatMillisToDisplayDateTimeString, unspecifiedDateToMillis } from 'utils/dateManagement';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { FormButton } from 'components/buttons/formButton';
import { renderTime } from 'utils/dayTimeHelpers';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { DateWrapper } from 'components/formInputs/wrappers/dateWrapper';
import { TimeWrapper } from 'components/formInputs/wrappers/timeWrapper';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TextboxTypeWrapper } from 'components/formInputs/wrappers/textboxTypeWrapper';
import { DataColumn, LinkColumn, List } from 'components/lists';
import { getTranslatedData } from 'locales';
import { updateRestrictedResources } from 'api/restaurant';
import { apiActionChooser } from 'utils/apiActionChooser';
import { PricingDataBlock } from '../pricing/components/dataBlock';
import { CouponProductAddEditForm } from './components/_popup_coupon_product';

const daysOptions = [
  { label: 'Monday', value: 'Mon' },
  { label: 'Tuesday', value: 'Tue' },
  { label: 'Wednesday', value: 'Wed' },
  { label: 'Thursday', value: 'Thu' },
  { label: 'Friday', value: 'Fri' },
  { label: 'Saturday', value: 'Sat' },
  { label: 'Sunday', value: 'Sun' },
];
const types = { FIXED: 'Fixed', PERCENTAGE: 'Percent' };

export const CouponAddEditForm = ({ itemData, isEdit }) => {
  const navigate = useNavigate();
  const { state: companyState, getCompanyRestaurants, addNew, editById } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);
  const [selectedDays, setSelectedDays] = useState(itemData?.days || []);
  const [selectedType, setSelectedType] = useState(types.PERCENTAGE);

  const [isApplyOnProducts, setIsApplyOnProducts] = useState(false);
  const [applicableProducts, setApplicableProducts] = useState([]);
  const [selectedApplicableProduct, setSelectedApplicableProduct] = useState(null);
  const [applicableProductModalIsVisible, setApplicableProductModalIsVisible] = useState(false);
  const [selectedApplicableProductInd, setSelectedApplicableProductInd] = useState(-1);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const productsRequestData = {
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: Config.DATA.AGGREGATES.GET_ALL_COMPANY_PRODUCTS,
    secretKey: auth.token,
    filter: { company: auth.company?.company },
    aggregateIdV2: 'AdminGetCompanyProducts',
    companyIdForV2: auth.company?.company,
  };

  const [, allProducts] = useAggregate(productsRequestData);

  const categoriesRequestData = {
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: Config.DATA.AGGREGATES.GET_ALL_COMPANY_CATEGORIES,
    secretKey: auth.token,
    filter: { company: auth.company?.company },
    aggregateIdV2: 'AdminGetCompanyCategories',
    companyIdForV2: auth.company?.company,
  };

  const [, allCategories] = useAggregate(categoriesRequestData);

  const getRestaurantsDataSource = () => {
    const filteredRestaurants = companyState.restaurants?.filter((c) => !selectedRestaurants.includes(c._id));
    const options = filteredRestaurants?.map((x) => ({
      label: x.name,
      value: x._id,
    }));

    options?.unshift({
      label: selectedRestaurants && selectedRestaurants.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const getDaysDataSource = () => {
    const filteredDays = daysOptions?.filter((c) => !selectedDays.includes(c.value));
    const options = filteredDays?.map((x) => ({
      label: x.label,
      value: x.value,
    }));

    options?.unshift({
      label: selectedDays && selectedDays.length > 0 ? '-' : 'All',
      value: '-1',
    });

    return options;
  };

  const parseDateTimeString = (current, original) => {
    if (original === '') {
      return null;
    }

    if (moment(original, 'YYYY-MM-DD HH:mm', true).isValid()) {
      return moment(original, 'YYYY-MM-DD HH:mm', true).toDate();
    }

    return current;
  };

  const convertMinutesToTimeString = (mins) => {
    if (mins === null || mins === undefined) {
      return '';
    }

    const hours = Math.trunc(mins / 60);
    const minutes = mins % 60;

    return renderTime(hours, minutes);
  };

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    coupon: yup.string().default(itemData?.coupon),
    description: yup.string().default(itemData?.description),

    valid_from: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseDateTimeString)
      .nullable(true)
      .default(itemData?.valid_from ? formatMillisToDisplayDateTimeString(itemData?.valid_from) : ''),
    valid_until: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseDateTimeString)
      .nullable(true)
      .default(itemData?.valid_until ? formatMillisToDisplayDateTimeString(itemData?.valid_until) : ''),

    day_minutes_from: yup.string().default(convertMinutesToTimeString(itemData?.day_minutes_from)),
    day_minutes_until: yup.string().default(convertMinutesToTimeString(itemData?.day_minutes_until)),

    discountValue: yup
      .number()
      .typeError('Please enter a valid number.')
      .nullable(false)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : -1))
      .default(itemData?.percent || itemData?.fixed),

    apply_on_products: yup.boolean().default(itemData?.apply_on_products || false),
    // applicable_products

    isEditForm: yup.boolean().default(isEdit),

  });

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData) {
      setSelectedType(itemData.use_fixed ? types.FIXED : types.PERCENTAGE);
      setSelectedRestaurants(itemData.restaurants || []);
      setSelectedDays(itemData.days || []);
      setIsApplyOnProducts(itemData.apply_on_products || false);
      setApplicableProducts(itemData.applicable_products || []);
    }
  }, [itemData]);

  const timeStringToDayMinutes = (timeString) => {
    if (!timeString) {
      return null;
    }

    const splitTimeParts = timeString.split(':');
    const h = parseInt(splitTimeParts[0], 10);
    const m = splitTimeParts.length > 1 ? parseInt(splitTimeParts[1], 10) : 0;

    return h * 60 + m;
  };

  const onSubmit = async (data) => {
    const submitData = {
      company: auth.company?.company,
      restaurants: data.restaurants,
      coupon: data.coupon,
      description: data.description,
      valid_from: data.valid_from ? unspecifiedDateToMillis(data.valid_from) : null,
      valid_until: data.valid_until ? unspecifiedDateToMillis(data.valid_until) : null,
      day_minutes_from: timeStringToDayMinutes(data.day_minutes_from),
      day_minutes_until: timeStringToDayMinutes(data.day_minutes_until),
      days: selectedDays,
      apply_on_products: data.apply_on_products,
    };

    if (submitData.apply_on_products) {
      submitData.applicable_products = applicableProducts || [];
      submitData.use_fixed = false;
      submitData.percent = 0;
      submitData.fixed = 0;
    } else {
      submitData.applicable_products = [];
      submitData.use_fixed = selectedType === types.FIXED;
      submitData.percent = selectedType === types.PERCENTAGE ? data.discountValue : 0;
      submitData.fixed = selectedType === types.FIXED ? data.discountValue : 0;
    }

    setIsSaving(true);

    if (!isEdit) {
      await apiActionChooser(auth.company?.company, async () => {
        const response = await updateRestrictedResources({
          token: auth.token,
          updateKey: 'coupon_manage',
          values: {
            type: isEdit ? 'Update' : 'Insert',
            id: itemData?._id,
            data: submitData,
          },
          alertController,
        });

        if (response && response.isAcknowledged) {
          alertController.success({ text: isEdit ? 'Coupon updated' : 'Coupon created' });
          navigate('/discounts');
        }
      }, async () => {
        await addNew(
          {
            data: submitData,
            resourceV2: 'AdminCoupon',
            companyIdForV2: auth.company?.company,
          },
          () => {
            alertController.success({ text: 'Coupon created' });
            navigate('/discounts');
          },
        );
      });
    } else {
      await apiActionChooser(auth.company?.company, async () => {
        const response = await updateRestrictedResources({
          token: auth.token,
          updateKey: 'coupon_manage',
          values: {
            type: isEdit ? 'Update' : 'Insert',
            id: itemData?._id,
            data: submitData,
          },
          alertController,
        });

        if (response && response.isAcknowledged) {
          alertController.success({ text: isEdit ? 'Coupon updated' : 'Coupon created' });
          navigate('/discounts');
        }
      }, async () => {
        await editById(
          {
            id: itemData?._id,
            resourceV2: 'AdminCoupon',
            updateTypeV2: 'General',
            updateDataV2: submitData,
            companyIdForV2: auth.company?.company,
          },
          () => {
            alertController.success({ text: 'Coupon updated' });
            navigate('/discounts');
          },
          () => {
            if (setIsSaving) {
              setIsSaving(false);
            }
          },
        );
      });
    }

    setIsSaving(false);
  };

  const onError = (data) => console.log(data);

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <ul className="mt-1">
        {selectedRestaurants.map((selected) => {
          const restaurant = companyState.restaurants?.find(
            (x) => x._id === selected,
          );

          if (!restaurant) return null;
          return (
            <div key={restaurant._id}>
              <SelectedItemButton
                name={restaurant?.name}
                onClick={() => {
                  const newRestaurants = selectedRestaurants.filter((x) => x !== restaurant?._id);

                  setSelectedRestaurants(newRestaurants);
                  setValue('restaurants', newRestaurants);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  const renderSelectedDays = () => {
    if (!selectedDays) return null;

    return (
      <ul className="mt-1">
        {selectedDays.map((selected) => {
          const day = daysOptions?.find(
            (x) => x.value === selected,
          );

          if (!day) return null;
          return (
            <div key={day.value}>
              <SelectedItemButton
                name={day?.label}
                onClick={() => {
                  const newDays = selectedDays.filter((x) => x !== day?.value);

                  setSelectedDays(newDays);
                  setValue('days', newDays);
                }}
              />
            </div>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      {applicableProductModalIsVisible && (
        <CouponProductAddEditForm
          itemData={selectedApplicableProduct}
          isOpen={applicableProductModalIsVisible}
          closeModal={() => setApplicableProductModalIsVisible(false)}
          onSaved={(data) => {
            setApplicableProductModalIsVisible(false);
            setApplicableProducts((prev) => {
              if (selectedApplicableProductInd === -1) {
                return [...prev, data];
              }

              // eslint-disable-next-line no-param-reassign
              prev[selectedApplicableProductInd] = data;
              return [...prev];
            });
          }}
          allProducts={allProducts}
          allCategories={allCategories}
        />
      )}

      <PageTitle
        title="Coupon"
        breadcrumbs={[{ path: '/discounts', title: 'Discounts & Coupons' }]}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="space-y-6">

              <PricingDataBlock title="Main" subtitle="Select applicable restaurants and set coupon code">
                <DropdownWrapper
                  name="restaurants"
                  title="Restaurants"
                  options={getRestaurantsDataSource()}
                  error={errors?.restaurants}
                  responsiveClassName="col-span-6 lg:col-span-4"
                  onChange={(e) => {
                    setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                    setValue('restaurants', [...selectedRestaurants, e.target.value]);
                  }}
                  renderSelections={renderSelectedRestaurants}
                />

                <TextboxWrapper
                  register={register}
                  name="coupon"
                  title="Coupon"
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                />

                <TextboxWrapper
                  register={register}
                  name="description"
                  title="Description"
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
                />
              </PricingDataBlock>

              <PricingDataBlock title="Days" subtitle="Set days when coupon is available">
                <DateWrapper
                  register={register}
                  name="valid_from"
                  title="Valid from (UTC +00:00)"
                  error={errors?.valid_from}
                  isDateTime
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                />

                <DateWrapper
                  register={register}
                  name="valid_until"
                  title="Valid until (UTC +00:00)"
                  error={errors?.valid_until}
                  isDateTime
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                />

                <TimeWrapper
                  register={register}
                  name="day_minutes_from"
                  title="Day time from"
                  error={errors?.day_minutes_from}
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                />
                <TimeWrapper
                  register={register}
                  name="day_minutes_until"
                  title="Day time until"
                  error={errors?.day_minutes_until}
                  responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
                />

                <DropdownWrapper
                  name="days"
                  title="Days"
                  options={getDaysDataSource()}
                  error={errors?.days}
                  responsiveClassName="col-span-6 lg:col-span-4"
                  onChange={(e) => {
                    setSelectedDays((oldArray) => [...oldArray, e.target.value]);
                    setValue('days', [...selectedDays, e.target.value]);
                  }}
                  renderSelections={renderSelectedDays}
                />
              </PricingDataBlock>

              <div className="bg-white shadow sm:rounded-md overflow-hidden mt-10">
                <div className="pt-6 px-4 space-y-6 sm:px-6">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Discounts</h3>
                    <p className="mt-1 text-sm text-gray-500">Select how to apply coupon discount</p>
                  </div>
                </div>

                <div className="px-4 sm:px-6 mb-6 mt-6">
                  <CheckboxWrapper
                    register={register}
                    name="apply_on_products"
                    title="Apply on products"
                    subtitle="select if coupon should be applied on products"
                    responsiveClassName="col-span-6"
                    onChange={(e) => {
                      setIsApplyOnProducts(e.target.checked);
                    }}
                  />
                </div>

                {!isApplyOnProducts && (
                <div className="px-4 sm:px-6 mb-6 max-w-md">
                  <TextboxTypeWrapper
                    register={register}
                    title="Amount*"
                    name="discountValue"
                    error={errors?.discountValue}
                    responsiveClassName="col-span-6 md:col-span-6 lg:col-span-4"
                    options={[
                      { label: 'Percentage', value: types.PERCENTAGE },
                      { label: 'Fixed', value: types.FIXED },
                    ]}
                    onChangeSelectedType={setSelectedType}
                    selectedType={selectedType}
                  />
                </div>
                )}

                {isApplyOnProducts && applicableProducts && applicableProducts.length > 0 && (
                <div className="px-4 sm:px-6 mb-6">
                  <List
                    keyField="_index"
                    source={applicableProducts}
                    onRowClick={(item, rowInd) => {
                      setSelectedApplicableProduct(item);
                      setApplicableProductModalIsVisible(true);
                      setSelectedApplicableProductInd(rowInd);
                    }}
                  >
                    <DataColumn
                      name="Products"
                      dbColumnName="products"
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                      render={(item) => {
                        if (!item.products || item.products.length === 0) {
                          return '';
                        }

                        const filtered = allProducts?.filter((x) => item.products.includes(x._id)).map((x) => getTranslatedData(x.name));

                        return filtered ? filtered.join(', ') : '';
                      }}
                    />
                    <DataColumn
                      name="Categories"
                      dbColumnName="categories"
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                      render={(item) => {
                        if (!item.categories || item.categories.length === 0) {
                          return '';
                        }

                        const filtered = allCategories?.filter((x) => item.categories.includes(x._id)).map((x) => getTranslatedData(x.name));

                        return filtered ? filtered.join(', ') : '';
                      }}
                    />
                    <DataColumn
                      name="Amount"
                      dbColumnName="percent_fixed"
                      render={(item) => {
                        if (item.use_fixed) {
                          return (item.fixed || 0).toFixed(2);
                        }

                        return `${(item.percent || 0).toFixed(2)} %`;
                      }}
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                    />
                    <DataColumn
                      name="Min. Qnt."
                      dbColumnName="min_quantity"
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                    />
                    <DataColumn
                      name="Apply on Qnt."
                      dbColumnName="apply_on_quantity"
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                    />
                    <DataColumn
                      name="Repeat"
                      dbColumnName="can_repeat_quantity_apply"
                      headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                    />
                    <LinkColumn
                      title="Delete"
                      onClick={(_, rowInd) => {
                        setApplicableProducts((prev) => {
                          // eslint-disable-next-line no-param-reassign
                          prev[rowInd] = null;

                          return [...prev.filter((x) => !!x)];
                        });
                      }}
                      className="relative py-3.5 pl-3 sm:pr-6"
                      dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                    />
                  </List>
                </div>
                )}
                {isApplyOnProducts && (!applicableProducts || applicableProducts.length === 0) && <div className="mb-6" />}

                {isApplyOnProducts && (
                <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedApplicableProduct(null);
                      setApplicableProductModalIsVisible(true);
                      setSelectedApplicableProductInd(-1);
                    }}
                    className="bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Add new
                  </button>
                </div>
                )}

              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate('/discounts')}
                >
                  Cancel
                </button>
                <FormButton text="Save" isLoading={isSaving} />
              </div>

            </div>
          </form>
        </div>
      </div>
    </>
  );
};
